import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiCreditCard, FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import { FaStripe } from "react-icons/fa";
import api, { FALLBACK_PLANS, formatCents } from "../services/api";

export default function Checkout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const demo = searchParams.get("demo") === "1";
  const planParam = (searchParams.get("plan") || "professional").toLowerCase();

  const plan = useMemo(
    () =>
      FALLBACK_PLANS.find((p) => p.id === planParam) ||
      FALLBACK_PLANS.find((p) => p.id === "professional"),
    [planParam],
  );

  // Card is the only way to pay, so this is fixed rather than chosen.
  const provider = "stripe";
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);

  const priceCents = plan.priceCents;

  // Stripe sends the customer back here with the session it just took payment
  // for. Nothing is trusted from the URL itself — the id is handed to the
  // server, which asks Stripe whether the money actually arrived.
  const sessionId =
    searchParams.get("session_id") || searchParams.get("session") || "";
  const [settling, setSettling] = useState(Boolean(sessionId));
  const [settled, setSettled] = useState(null);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      const res = await api.confirmCheckout(sessionId);
      if (cancelled) return;
      setSettling(false);
      setSettled(res);
      if (res.ok && res.paid) {
        setTimeout(() => {
          navigate(`/pending-approval?email=${encodeURIComponent(email)}`);
        }, 2500);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, email, navigate]);

  async function handleConfirm() {
    setLoading(true);
    setNotice(null);
    const res = await api.checkout({
      email,
      planId: plan.id,
      provider,
    });
    setLoading(false);

    const next = `/pending-approval?email=${encodeURIComponent(email)}`;

    // Stripe hosts the payment page, so the card is entered there and never
    // touches this site. Stripe returns the customer to the success URL with
    // the session id, which is confirmed against Stripe when they land back.
    if (res.ok && res.checkoutUrl) {
      window.location.href = res.checkoutUrl;
      return;
    }
    if (res.ok) {
      navigate(next);
    } else if (res.offline) {
      navigate(`${next}&demo=1`);
    } else {
      setNotice(res.message || "Checkout failed. Please try again.");
    }
  }

  // ---- coming back from Stripe ----------------------------------------
  if (sessionId) {
    const paid = Boolean(settled && settled.ok && settled.paid);
    return (
      <section className="section-container flex justify-center py-16">
        <div className="card w-full max-w-xl p-8 text-center">
          {settling && (
            <>
              <h1 className="text-2xl font-bold">Confirming your payment</h1>
              <p className="mt-2 text-sm text-slate-600">
                Checking with Stripe — this only takes a moment.
              </p>
            </>
          )}

          {!settling && paid && (
            <>
              <FiCheckCircle className="mx-auto text-emerald-500" size={44} />
              <h1 className="mt-4 text-2xl font-bold">Payment received</h1>
              <p className="mt-2 text-sm text-slate-600">
                Thank you. You paid {formatCents(priceCents)} for a year of the{" "}
                {plan.name} plan.
              </p>
              <p className="mt-3 text-sm text-slate-600">
                Your account is pending administrator approval. Taking you there
                now…
              </p>
            </>
          )}

          {!settling && !paid && (
            <>
              <FiAlertCircle className="mx-auto text-amber-500" size={44} />
              <h1 className="mt-4 text-2xl font-bold">
                We couldn&rsquo;t confirm that payment
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                {(settled && settled.message) ||
                  "Stripe has not confirmed this payment yet."}
              </p>
              <button
                type="button"
                onClick={() => navigate("/pricing")}
                className="btn-primary mt-6"
              >
                Back to pricing
              </button>
            </>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="section-container flex justify-center py-16">
      <div className="w-full max-w-xl">
        <h1 className="text-center text-2xl font-bold">Complete your order</h1>
        <p className="mt-1 text-center text-sm text-slate-600">
          Payment is handled by Stripe. Your card details never reach this site.
        </p>

        {demo && (
          <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
            Demo mode: the server is unreachable, but you can continue and
            explore the flow.
          </div>
        )}

        {/* Order summary */}
        <div className="card mt-6 p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            Order Summary
          </h2>

          <div className="mt-4 flex items-center justify-between">
            <div>
              <p className="text-base font-semibold text-navy">
                Money Flow Accounting {plan.name}
              </p>
              <p className="text-xs text-slate-500">{plan.description}</p>
            </div>
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600">
              Billed yearly
            </span>
          </div>

          <div className="mt-5 flex items-baseline justify-between border-t border-line pt-5">
            <span className="text-sm text-slate-600">
              Total due today
              <span className="ml-2 text-xs text-slate-500">then yearly</span>
            </span>
            <span className="text-2xl font-extrabold text-navy">
              {formatCents(priceCents)}
            </span>
          </div>

          <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
            <FiCheckCircle className="text-emerald-500" size={13} />
            Billed once a year. Renews automatically — cancel any time.
          </p>
        </div>

        {/* Payment method */}
        <div className="card mt-6 p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            Payment Method
          </h2>
          {/* Card only. PayPal was removed rather than left on the page: it
              recorded a plan without ever taking the money. */}
          <div className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-primary bg-primary/5 p-4 text-sm font-semibold text-navy ring-1 ring-primary">
            <FaStripe size={30} className="text-[#635BFF]" />
            <span className="inline-flex items-center gap-1.5">
              <FiCreditCard size={15} /> Card
            </span>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Your card is entered on Stripe's secure page — it never reaches this
            site.
          </p>
        </div>

        {notice && (
          <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {notice}
          </div>
        )}

        <button
          type="button"
          onClick={handleConfirm}
          disabled={loading}
          className="btn-primary mt-6 w-full disabled:opacity-60"
        >
          {loading ? "Processing..." : "Continue to Payment"}
        </button>
      </div>
    </section>
  );
}
